/**
 * Storage key for Flow tracer enabled-on-load preference.
 */
export const STORAGE_KEY_FLOW_ENABLED_ON_LOAD = "autotracer-flow-enabled-on-load";
/**
 * Storage key for Flow tracer auto-stop after N top-level functions.
 */
export const STORAGE_KEY_FLOW_AUTOSTOP_TOPLEVEL = "autotracer-flow-autostop-toplevel";
/**
 * Storage key for Flow tracer auto-stop after N total functions.
 */
export const STORAGE_KEY_FLOW_AUTOSTOP_ALL = "autotracer-flow-autostop-all";
/**
 * Storage key for Flow tracer start trigger pattern.
 */
export const STORAGE_KEY_FLOW_START_TRIGGER = "autotracer-flow-start-trigger";
/**
 * Storage key for Flow tracer end trigger pattern.
 */
export const STORAGE_KEY_FLOW_END_TRIGGER = "autotracer-flow-end-trigger";
/**
 * Storage key for Flow tracer end trigger mode.
 */
export const STORAGE_KEY_FLOW_END_TRIGGER_MODE = "autotracer-flow-end-trigger-mode";
/**
 * Storage key for Flow tracer trigger re-arm mode.
 */
export const STORAGE_KEY_FLOW_TRIGGER_REARM = "autotracer-flow-trigger-rearm";
/**
 * Get enabledOnLoad state from localStorage.
 *
 * @returns True if enabled, false if disabled, null if never set
 */
export function getEnabledOnLoadFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_ENABLED_ON_LOAD);
        if (value === null)
            return null;
        return value === "true";
    }
    catch {
        return null;
    }
}
/**
 * Save enabledOnLoad state to localStorage.
 *
 * @param enabled - Whether tracer should auto-start on load
 */
export function saveEnabledOnLoadToStorage(enabled) {
    try {
        localStorage.setItem(STORAGE_KEY_FLOW_ENABLED_ON_LOAD, String(enabled));
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get auto-stop top-level limit from localStorage.
 *
 * @returns Top-level function limit, or null if not set
 */
export function getAutoStopTopLevelFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_AUTOSTOP_TOPLEVEL);
        if (value === null)
            return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) || parsed <= 0 ? null : parsed;
    }
    catch {
        return null;
    }
}
/**
 * Save auto-stop top-level limit to localStorage.
 *
 * @param limit - Number of top-level functions before auto-stop, or null to disable
 */
export function saveAutoStopTopLevelToStorage(limit) {
    try {
        if (limit === null || limit <= 0) {
            localStorage.removeItem(STORAGE_KEY_FLOW_AUTOSTOP_TOPLEVEL);
        }
        else {
            localStorage.setItem(STORAGE_KEY_FLOW_AUTOSTOP_TOPLEVEL, String(limit));
        }
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get auto-stop all functions limit from localStorage.
 *
 * @returns Total function limit, or null if not set
 */
export function getAutoStopAllFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_AUTOSTOP_ALL);
        if (value === null)
            return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) || parsed <= 0 ? null : parsed;
    }
    catch {
        return null;
    }
}
/**
 * Save auto-stop all functions limit to localStorage.
 *
 * @param limit - Number of total functions before auto-stop, or null to disable
 */
export function saveAutoStopAllToStorage(limit) {
    try {
        if (limit === null || limit <= 0) {
            localStorage.removeItem(STORAGE_KEY_FLOW_AUTOSTOP_ALL);
        }
        else {
            localStorage.setItem(STORAGE_KEY_FLOW_AUTOSTOP_ALL, String(limit));
        }
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get start trigger pattern from localStorage.
 *
 * @returns Trigger pattern string, or null if not set
 */
export function getStartTriggerFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_START_TRIGGER);
        return value === null || value.trim().length === 0 ? null : value;
    }
    catch {
        return null;
    }
}
/**
 * Save start trigger pattern to localStorage.
 *
 * @param pattern - Trigger pattern, or null to disable
 */
export function saveStartTriggerToStorage(pattern) {
    try {
        if (pattern === null || pattern.trim().length === 0) {
            localStorage.removeItem(STORAGE_KEY_FLOW_START_TRIGGER);
        }
        else {
            localStorage.setItem(STORAGE_KEY_FLOW_START_TRIGGER, pattern);
        }
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get end trigger pattern from localStorage.
 *
 * @returns Trigger pattern string, or null if not set
 */
export function getEndTriggerFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_END_TRIGGER);
        return value === null || value.trim().length === 0 ? null : value;
    }
    catch {
        return null;
    }
}
/**
 * Save end trigger pattern to localStorage.
 *
 * @param pattern - Trigger pattern, or null to disable
 */
export function saveEndTriggerToStorage(pattern) {
    try {
        if (pattern === null || pattern.trim().length === 0) {
            localStorage.removeItem(STORAGE_KEY_FLOW_END_TRIGGER);
        }
        else {
            localStorage.setItem(STORAGE_KEY_FLOW_END_TRIGGER, pattern);
        }
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get end trigger mode from localStorage.
 *
 * @returns End trigger mode, or "on-exit" as default
 */
export function getEndTriggerModeFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_END_TRIGGER_MODE);
        return value === "on-entry" ? "on-entry" : "on-exit";
    }
    catch {
        return "on-exit";
    }
}
/**
 * Save end trigger mode to localStorage.
 *
 * @param mode - End trigger mode
 */
export function saveEndTriggerModeToStorage(mode) {
    try {
        localStorage.setItem(STORAGE_KEY_FLOW_END_TRIGGER_MODE, mode);
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Get trigger re-arm mode from localStorage.
 *
 * @returns Trigger re-arm mode, or "once" as default
 */
export function getTriggerRearmModeFromStorage() {
    try {
        const value = localStorage.getItem(STORAGE_KEY_FLOW_TRIGGER_REARM);
        return value === "always" ? "always" : "once";
    }
    catch {
        return "once";
    }
}
/**
 * Save trigger re-arm mode to localStorage.
 *
 * @param mode - Trigger re-arm mode
 */
export function saveTriggerRearmModeToStorage(mode) {
    try {
        localStorage.setItem(STORAGE_KEY_FLOW_TRIGGER_REARM, mode);
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Clear all trigger settings from localStorage.
 */
export function clearAllTriggersFromStorage() {
    try {
        localStorage.removeItem(STORAGE_KEY_FLOW_START_TRIGGER);
        localStorage.removeItem(STORAGE_KEY_FLOW_END_TRIGGER);
        localStorage.removeItem(STORAGE_KEY_FLOW_END_TRIGGER_MODE);
        localStorage.removeItem(STORAGE_KEY_FLOW_TRIGGER_REARM);
    }
    catch {
        // Ignore localStorage errors
    }
}
/**
 * Creates runtime control API for a Flow logger.
 *
 * @param logger - Logger instance for enable/disable control
 * @returns Basic runtime controls (subset of FlowTracerAPI)
 */
export function createRuntimeControl(logger) {
    let currentConfig = {};
    let isActive = false;
    /**
     * Start flow tracing.
     */
    function start(config = {}) {
        // Merge with existing config
        currentConfig = { ...currentConfig, ...config };
        // Enable logger
        logger.setLogLevel("trace");
        isActive = true;
        logger.log("Flow tracing started");
    }
    /**
     * Stop flow tracing.
     */
    function stop() {
        // Log message before disabling logger
        logger.log("Flow tracing stopped");
        // Disable logger
        logger.setLogLevel("off");
        isActive = false;
    }
    /**
     * Check if tracing is enabled.
     */
    function isEnabled() {
        return isActive;
    }
    /**
     * Get current configuration.
     */
    function getConfig() {
        return { ...currentConfig };
    }
    /**
     * Update configuration.
     */
    function setConfig(config) {
        currentConfig = { ...currentConfig, ...config };
    }
    /**
     * Enable tracer on page load.
     */
    function setEnabledOnLoad(value) {
        saveEnabledOnLoadToStorage(value);
    }
    /**
     * Check if tracer is configured to start on page load.
     */
    function getEnabledOnLoad() {
        return getEnabledOnLoadFromStorage() ?? false;
    }
    return {
        start,
        stop,
        isEnabled,
        getConfig,
        setConfig,
        setEnabledOnLoad,
        getEnabledOnLoad,
    };
}
